/**
 * Centralized Validation Configuration
 * All validation constants, limits, and rules in one place
 */

/**
 * Business Validation Limits
 * These values define the business rules for the application
 */
export const VALIDATION_LIMITS = {
  // Electricity Billing Limits
  ELECTRICITY: {
    KVA_DEMAND: {
      MIN: 0,
      MAX: 10000,
      STEP: 0.01,
    },
    KWH_CONSUMPTION: {
      MIN: 0,
      MAX: 1000000,
      STEP: 0.1,
    },
    BILL_AMOUNT: {
      MIN: 0,
      MAX: 1000000000,
      STEP: 0.01,
    },
    METER_READING: {
      MIN: 0,
      MAX: 999999999,
      STEP: 0.1,
    },
  },

  // DG (Diesel Generator) Billing Limits
  DG: {
    DIESEL_CONSUMPTION: {
      MIN: 0.1, // Must be greater than 0
      MAX: 100000,
      STEP: 0.1,
    },
    RUNNING_HOURS: {
      MIN: 0.1, // Must be greater than 0
      MAX: 8760, // Maximum hours in a year
      STEP: 0.1,
    },
    DG_KWH_CONSUMPTION: {
      MIN: 0,
      MAX: 500000,
      STEP: 0.1,
    },
    EFFICIENCY: {
      KWH_PER_LITRE: {
        MIN: 0.5,
        MAX: 10.0,
        EXCELLENT: 3.5,
        GOOD: 3.0,
        AVERAGE: 2.5,
        POOR: 2.0,
      },
    },
  },

  // Rate Master Limits
  RATES: {
    RATE_VALUE: {
      MIN: 0,
      MAX: 10000,
      STEP: 0.0001,
    },
    PERCENTAGE: {
      MIN: 0,
      MAX: 100,
      STEP: 0.01,
    },
  },

  // General Limits
  GENERAL: {
    NOTES: {
      MAX_LENGTH: 500,
    },
    VARIANCE_PERCENTAGE: {
      EXCELLENT: 1,
      GOOD: 5,
      WARNING: 10,
    },
  },
};

/**
 * Validation Error Messages
 * Centralized error messages for consistency
 */
export const VALIDATION_MESSAGES = {
  REQUIRED: (field) => `${field} is required`,
  INVALID_NUMBER: (field) => `${field} must be a valid number`,
  MIN_VALUE: (field, min) => `${field} must be at least ${min}`,
  MAX_VALUE: (field, max) => `${field} must not exceed ${max}`,
  POSITIVE_NUMBER: (field) => `${field} must be a positive number`,
  NON_NEGATIVE: (field) => `${field} must be non-negative`,

  // Specific field messages
  KVA_DEMAND_HIGH: `kVA demand seems unusually high (max ${VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.MAX.toLocaleString()} kVA)`,
  DIESEL_HIGH: `Diesel consumption seems unusually high (max ${VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MAX.toLocaleString()} litres)`,
  RUNNING_HOURS_HIGH: `Running hours cannot exceed ${VALIDATION_LIMITS.DG.RUNNING_HOURS.MAX.toLocaleString()} hours per year`,
  BILL_AMOUNT_HIGH: 'Bill amount seems unusually high',
  KWH_HIGH: `kWh consumption seems unusually high (max ${VALIDATION_LIMITS.ELECTRICITY.KWH_CONSUMPTION.MAX.toLocaleString()} kWh)`,
  NOTES_TOO_LONG: `Notes must be ${VALIDATION_LIMITS.GENERAL.NOTES.MAX_LENGTH} characters or less`,
};

/**
 * Validation Rule Generators
 * Functions that generate validation rules using the centralized limits
 */
export const VALIDATION_RULES = {
  // Required field validation
  required: (fieldName) => [
    (v) => (
      v !== null
      && v !== undefined
      && v !== ''
    ) || VALIDATION_MESSAGES.REQUIRED(fieldName),
  ],

  // Number validation with min/max
  number: (fieldName, min = null, max = null, allowZero = true) => {
    const rules = [
      (v) => (
        v !== null
        && v !== undefined
        && v !== ''
      ) || VALIDATION_MESSAGES.REQUIRED(fieldName),
      (v) => !Number.isNaN(parseFloat(v)) || VALIDATION_MESSAGES.INVALID_NUMBER(fieldName),
    ];

    if (!allowZero) {
      rules.push((v) => parseFloat(v) > 0 || VALIDATION_MESSAGES.POSITIVE_NUMBER(fieldName));
    } else if (min !== null) {
      rules.push((v) => parseFloat(v) >= min || VALIDATION_MESSAGES.MIN_VALUE(fieldName, min));
    } else {
      rules.push((v) => parseFloat(v) >= 0 || VALIDATION_MESSAGES.NON_NEGATIVE(fieldName));
    }

    if (max !== null) {
      rules.push((v) => parseFloat(v) <= max || VALIDATION_MESSAGES.MAX_VALUE(fieldName, max));
    }

    return rules;
  },

  // Electricity-specific validation rules
  kvaDemand: () => VALIDATION_RULES.number(
    'kVA demand',
    VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.MIN,
    VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.MAX,
  ).concat([
    (v) => (
      parseFloat(v) <= VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.MAX
    ) || VALIDATION_MESSAGES.KVA_DEMAND_HIGH,
  ]),

  actualBillAmount: () => VALIDATION_RULES.number(
    'Actual bill amount',
    VALIDATION_LIMITS.ELECTRICITY.BILL_AMOUNT.MIN,
    VALIDATION_LIMITS.ELECTRICITY.BILL_AMOUNT.MAX,
  ).concat([
    (v) => (
      parseFloat(v) <= VALIDATION_LIMITS.ELECTRICITY.BILL_AMOUNT.MAX
    ) || VALIDATION_MESSAGES.BILL_AMOUNT_HIGH,
  ]),

  kwhConsumption: () => VALIDATION_RULES.number(
    'kWh consumption',
    VALIDATION_LIMITS.ELECTRICITY.KWH_CONSUMPTION.MIN,
    VALIDATION_LIMITS.ELECTRICITY.KWH_CONSUMPTION.MAX,
  ).concat([
    (v) => (
      parseFloat(v) <= VALIDATION_LIMITS.ELECTRICITY.KWH_CONSUMPTION.MAX
    ) || VALIDATION_MESSAGES.KWH_HIGH,
  ]),

  // DG-specific validation rules
  dieselConsumption: () => VALIDATION_RULES.number(
    'Diesel consumption',
    VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MIN,
    VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MAX,
    false, // Must be greater than 0
  ).concat([
    (v) => (
      parseFloat(v) <= VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MAX
    ) || VALIDATION_MESSAGES.DIESEL_HIGH,
  ]),

  runningHours: () => VALIDATION_RULES.number(
    'Running hours',
    VALIDATION_LIMITS.DG.RUNNING_HOURS.MIN,
    VALIDATION_LIMITS.DG.RUNNING_HOURS.MAX,
    false, // Must be greater than 0
  ).concat([
    (v) => (
      parseFloat(v) <= VALIDATION_LIMITS.DG.RUNNING_HOURS.MAX
    ) || VALIDATION_MESSAGES.RUNNING_HOURS_HIGH,
  ]),

  // General validation rules
  notes: () => [
    (v) => (
      !v
      || v.length <= VALIDATION_LIMITS.GENERAL.NOTES.MAX_LENGTH
    ) || VALIDATION_MESSAGES.NOTES_TOO_LONG,
  ],

  percentage: (fieldName) => VALIDATION_RULES.number(
    fieldName,
    VALIDATION_LIMITS.RATES.PERCENTAGE.MIN,
    VALIDATION_LIMITS.RATES.PERCENTAGE.MAX,
  ),
};

/**
 * Efficiency Rating Helper
 * Determines efficiency rating based on kWh/L value
 */
export const getEfficiencyRating = (kwhPerLitre) => {
  const limits = VALIDATION_LIMITS.DG.EFFICIENCY.KWH_PER_LITRE;

  if (kwhPerLitre >= limits.EXCELLENT) return 'Excellent';
  if (kwhPerLitre >= limits.GOOD) return 'Good';
  if (kwhPerLitre >= limits.AVERAGE) return 'Average';
  return 'Poor';
};

/**
 * Variance Rating Helper
 * Determines variance rating based on percentage
 */
export const getVarianceRating = (variancePercentage) => {
  const limits = VALIDATION_LIMITS.GENERAL.VARIANCE_PERCENTAGE;

  if (variancePercentage <= limits.EXCELLENT) return 'Excellent';
  if (variancePercentage <= limits.GOOD) return 'Good';
  if (variancePercentage <= limits.WARNING) return 'Warning';
  return 'Poor';
};

/**
 * Input Field Configuration
 * Provides consistent input field configurations
 */
export const INPUT_CONFIG = {
  kvaDemand: {
    type: 'number',
    step: VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.STEP,
    min: VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.MIN,
    max: VALIDATION_LIMITS.ELECTRICITY.KVA_DEMAND.MAX,
    suffix: 'kVA',
    rules: VALIDATION_RULES.kvaDemand(),
  },

  actualBillAmount: {
    type: 'number',
    step: VALIDATION_LIMITS.ELECTRICITY.BILL_AMOUNT.STEP,
    min: VALIDATION_LIMITS.ELECTRICITY.BILL_AMOUNT.MIN,
    suffix: 'Currency', // Will be replaced by actual currency symbol
    rules: VALIDATION_RULES.actualBillAmount(),
  },

  dieselConsumption: {
    type: 'number',
    step: VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.STEP,
    min: VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MIN,
    max: VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MAX,
    suffix: 'Litres',
    rules: VALIDATION_RULES.dieselConsumption(),
  },

  runningHours: {
    type: 'number',
    step: VALIDATION_LIMITS.DG.RUNNING_HOURS.STEP,
    min: VALIDATION_LIMITS.DG.RUNNING_HOURS.MIN,
    max: VALIDATION_LIMITS.DG.RUNNING_HOURS.MAX,
    suffix: 'Hours',
    rules: VALIDATION_RULES.runningHours(),
  },

  notes: {
    type: 'textarea',
    maxLength: VALIDATION_LIMITS.GENERAL.NOTES.MAX_LENGTH,
    counter: VALIDATION_LIMITS.GENERAL.NOTES.MAX_LENGTH,
    rules: VALIDATION_RULES.notes(),
  },
};

export default {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  VALIDATION_RULES,
  INPUT_CONFIG,
  getEfficiencyRating,
  getVarianceRating,
};
